import { X } from "lucide-react";
import { memo, useId } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import { useTransferPopupCopy } from "./hooks/use-transfer-popup-copy";
import { TransferPopupProgress } from "./TransferPopupProgress";
import { TransferPopupStatusIcon } from "./TransferPopupStatusIcon";
import type { ITransferPopupCardProps } from "./types";
import { ETransferPopupVariant } from "./types";

// NOTE: NSAI-7067 — Pavel's NSAI-5939 card: a frosted bubble with the phase
// glyph, the file and what is happening to it, "120 MB of 320 MB · 2 min
// left", a bar with its percentage, and an X. Rendered as liquid glass — a
// translucent fill over a heavy backdrop blur, a hairline rim that catches
// light along the top edge, and a soft sheen — so it reads as a layer floating
// over the page rather than a block cut into it. The tokens are the ones the
// image viewer's floating controls already use (`border-border/40`,
// `bg-background/75`, `backdrop-blur-xl`), pushed one step further.
const TransferPopupCardComponent = ({
  variant,
  primary,
  selection,
  now,
  onOpenUploads,
  onClose,
}: ITransferPopupCardProps): JSX.Element => {
  const { t } = useTranslation("uploads");
  const titleId = useId();
  const isMultiple = selection.inFlightCount > 1;
  const { title, subtitle, closeAriaLabel, isCloseCancelling } =
    useTransferPopupCopy({ primary, selection, now });

  return (
    <div
      role="status"
      aria-live="polite"
      aria-labelledby={titleId}
      className={cn(
        "group/transfer-popup relative isolate rounded-2xl p-2",
        "border border-white/50 bg-background/75 ring-1 ring-primary/[0.08] backdrop-blur-2xl backdrop-saturate-150",
        "shadow-[inset_0_1px_0_hsl(0_0%_100%/0.55),0_1px_2px_hsl(var(--foreground)/0.06),0_16px_40px_-16px_hsl(var(--foreground)/0.35)]",
        "dark:border-white/10 dark:bg-background/55 dark:shadow-[inset_0_1px_0_hsl(0_0%_100%/0.08),0_16px_40px_-16px_hsl(0_0%_0%/0.7)]",
        "animate-in fade-in-0 slide-in-from-bottom-2 duration-300 motion-reduce:animate-none",
        variant === ETransferPopupVariant.FLOATING && "w-[22rem] max-w-full",
      )}
      data-testid="transfer-popup"
      data-variant={variant}
      data-status={primary.status}
    >
      {/* NOTE: The sheen — light pooling in the top corner and a breath of
          the brand colour in the opposite one. Decorative only. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-px -z-10 rounded-[15px] bg-gradient-to-br from-white/70 via-primary/[0.03] to-primary/[0.14] dark:from-white/[0.08] dark:via-transparent dark:to-primary/20"
      />

      <div className="flex items-stretch gap-1">
        {/* NOTE: The body is one button — the card's whole surface is the
            way back to the full per-file list on the Uploads page. The close
            control sits beside it, not inside it, so the two never nest. */}
        <button
          type="button"
          onClick={onOpenUploads}
          aria-label={t("popup.open_uploads_aria")}
          className={cn(
            "group/transfer-body flex min-w-0 flex-1 cursor-pointer items-start gap-3 rounded-xl p-1 pe-2 text-start outline-none",
            "transition-colors duration-200 hover:bg-foreground/[0.035] focus-visible:bg-foreground/[0.04] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring motion-reduce:transition-none",
          )}
          data-testid="transfer-popup-open-uploads"
        >
          <TransferPopupStatusIcon primary={primary} isMultiple={isMultiple} />
          <div className="min-w-0 flex-1 pt-0.5">
            <p
              id={titleId}
              className="truncate text-sm font-semibold text-foreground"
              title={isMultiple ? undefined : primary.fileName}
              data-testid="transfer-popup-title"
            >
              {title}
            </p>
            {subtitle && (
              <p
                className="mt-0.5 truncate text-xs text-muted-foreground tabular-nums"
                title={subtitle}
                data-testid="transfer-popup-subtitle"
              >
                {subtitle}
              </p>
            )}
            <TransferPopupProgress
              primary={primary}
              selection={selection}
              isMultiple={isMultiple}
              labelledById={titleId}
            />
          </div>
        </button>

        <button
          type="button"
          onClick={onClose}
          aria-label={closeAriaLabel}
          className={cn(
            "mt-1 flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-transparent text-muted-foreground",
            "transition-all duration-200 hover:border-foreground/[0.06] hover:bg-foreground/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring motion-reduce:transition-none",
            // NOTE: Destructive only when the click aborts a transfer;
            // hiding a settled card is a neutral act.
            isCloseCancelling
              ? "hover:text-destructive"
              : "hover:text-foreground",
          )}
          data-testid="transfer-popup-close"
        >
          <X className="size-4 transition-transform duration-200 group-hover/transfer-popup:scale-105 motion-reduce:transition-none" />
        </button>
      </div>
    </div>
  );
};

export const TransferPopupCard = memo(TransferPopupCardComponent);
